import { Search, Upload } from "lucide-react";
import { useState } from "react";

import { useListUnmatchedPatientUploads } from "@elation/api-client/src/stitch-web";
import type { UnmatchedPatientUpload } from "@elation/api-client/src/stitch-web/models";

import { DocumentDropzoneTrigger } from "@src/features/documents";
import { Button } from "@src/shared/components/ui/button";
import { Input } from "@src/shared/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@src/shared/components/ui/select";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@src/shared/components/ui/tabs";
import {
  isStaleCursorError,
  shouldRetryCursorQueryError,
  useResetStaleCursor,
} from "@src/shared/hooks/useCursorPagination";

import { BulkCareGapDocumentDropzone } from "../BulkCareGapDocumentDropzone";
import { AllUploadHistoryList } from "./components/AllUploadHistoryList";
import {
  ExtractedCareGapsContent,
  useExtractedCareGapsUploadState,
} from "./components/ExtractedCareGapsTab";
import { NeedsReviewPatientUploadsList } from "./components/NeedsReviewPatientUploadsList";
import type { UploadHistoryWindow } from "./components/uploadHistoryWindow";

export interface NeedsReviewState {
  documents: UnmatchedPatientUpload[];
  isError: boolean;
  isLoading: boolean;
}

type UploadHistoryTab = "all" | "extracted" | "needs-review";
type UploadHistoryRangeDays = 1 | 7 | 14 | 30;

const uploadHistoryRangeOptions = [
  { label: "Last day", value: 1 },
  { label: "Last 7 days", value: 7 },
  { label: "Last 14 days", value: 14 },
  { label: "Last 30 days", value: 30 },
] satisfies Array<{ label: string; value: UploadHistoryRangeDays }>;

export function CareProtocolOpportunityHistoryListV2() {
  const [activeTab, setActiveTab] = useState<UploadHistoryTab>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [rangeDays, setRangeDays] = useState<UploadHistoryRangeDays>(30);
  const uploadHistoryWindow = {
    windowSize: rangeDays,
    windowUnit: "days",
  } satisfies UploadHistoryWindow;
  const extracted = useExtractedCareGapsUploadState({
    shouldPoll: activeTab === "all" || activeTab === "extracted",
    window: uploadHistoryWindow,
  });
  const shouldPollCurrentWindow =
    !extracted.currentCursor &&
    (activeTab === "all" || activeTab === "needs-review");
  const needsReviewQuery = useListUnmatchedPatientUploads(
    { cursor: extracted.currentCursor, ...uploadHistoryWindow },
    {
      query: {
        refetchInterval: shouldPollCurrentWindow ? 5_000 : false,
        retry: shouldRetryCursorQueryError,
      },
    },
  );
  const isStaleNeedsReviewCursor = isStaleCursorError({
    currentCursor: extracted.currentCursor,
    error: needsReviewQuery.error,
    isError: needsReviewQuery.isError,
  });
  const needsReviewCount = needsReviewQuery.data?.data.documents.length ?? 0;
  const needsReview = {
    documents: needsReviewQuery.data?.data.documents ?? [],
    isError: needsReviewQuery.isError && !isStaleNeedsReviewCursor,
    isLoading: needsReviewQuery.isLoading || isStaleNeedsReviewCursor,
  } satisfies NeedsReviewState;

  useResetStaleCursor(isStaleNeedsReviewCursor, extracted.resetCursor);

  const handleRangeChange = (value: string) => {
    const nextRangeDays = Number(value) as UploadHistoryRangeDays;
    setRangeDays(nextRangeDays);
    extracted.resetCursor();
  };

  return (
    <BulkCareGapDocumentDropzone
      className="flex min-h-0 flex-1"
      contentClassName="flex min-h-0 flex-1 flex-col"
    >
      <div className="flex min-h-0 w-full flex-1 flex-col">
        <header className="mb-3 flex shrink-0 items-start justify-between gap-3">
          <div className="min-w-0">
            <h1 className="text-[19px] font-semibold leading-tight tracking-[-0.018em] text-surface-foreground">
              Upload History
            </h1>
            <p className="mt-1 text-[13px] leading-5 text-muted-foreground">
              Each row is one extracted file. Expand a row to see its care gaps.
            </p>
          </div>
          <DocumentDropzoneTrigger ariaLabel="Upload care gap files">
            {({ isUploading, triggerProps }) => (
              <Button
                {...triggerProps}
                size="sm"
                className="h-8 shrink-0 rounded-[8px] px-3 text-[12px] font-semibold"
              >
                <Upload className="size-3.5" aria-hidden="true" />
                {isUploading ? "Uploading..." : "Upload files"}
              </Button>
            )}
          </DocumentDropzoneTrigger>
        </header>

        <div className="relative mb-2 shrink-0">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden="true"
          />
          <Input
            aria-label="Search upload history"
            className="h-9 rounded-[9px] bg-background pl-9 text-[13px]"
            placeholder="Search for file name or patient"
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
          />
        </div>

        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as UploadHistoryTab)}
          className="flex min-h-0 w-full flex-1 flex-col"
        >
          <div className="mb-3 flex shrink-0 gap-2">
            <TabsList className="grid h-[34px] flex-1 grid-cols-3 rounded-[10px] bg-muted/70 p-1">
              <TabsTrigger className="h-6 text-[12px]" value="all">
                All
              </TabsTrigger>
              <TabsTrigger className="h-6 text-[12px]" value="extracted">
                Extracted
              </TabsTrigger>
              <TabsTrigger
                className="h-6 gap-1 text-[12px]"
                value="needs-review"
              >
                Needs review
                {needsReviewCount > 0 ? (
                  <span className="font-mono text-[10px] text-primary">
                    {needsReviewCount}
                  </span>
                ) : null}
              </TabsTrigger>
            </TabsList>
            <Select value={String(rangeDays)} onValueChange={handleRangeChange}>
              <SelectTrigger
                aria-label="Upload history date range"
                className="h-[34px] w-[128px] rounded-[10px] bg-background text-[12px]"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent align="end">
                {uploadHistoryRangeOptions.map((option) => (
                  <SelectItem key={option.value} value={String(option.value)}>
                    {option.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <TabsContent value="all" className="mt-0 min-h-0 flex-1">
            <AllUploadHistoryList
              isVisible={activeTab === "all"}
              extracted={extracted}
              needsReview={needsReview}
              searchTerm={searchTerm}
            />
          </TabsContent>
          <TabsContent value="extracted" className="mt-0 min-h-0 flex-1">
            <ExtractedCareGapsContent
              isVisible={activeTab === "extracted"}
              searchTerm={searchTerm}
              shouldPollForDocuments={extracted.activeUploadCount > 0}
              state={extracted}
            />
          </TabsContent>
          <TabsContent value="needs-review" className="mt-0 min-h-0 flex-1">
            <NeedsReviewPatientUploadsList
              documents={needsReview.documents}
              isError={needsReview.isError}
              isLoading={needsReview.isLoading}
              searchTerm={searchTerm}
            />
          </TabsContent>
        </Tabs>
      </div>
    </BulkCareGapDocumentDropzone>
  );
}
